import React, { useState, useEffect, useMemo } from "react";
import Page from "components/Page";
import constants from "../../../../constants";
import styles from "../../../Ofiices/AddOffice/EditTabs.module.scss";
import accountPrefrencesStyles from "./../../accountpreferences.module.scss";
import Input from "components/Input";
import cameraIconWithBg from "../../../../assets/images/camera-icon-with-bg.svg";
import Text from "components/Text";
import { withTranslation } from "react-i18next";
import BrandEmailTemplate from "pages/Ofiices/AddOffice/BrandEmailTemplate";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { useSelector } from "react-redux";
import {
  addOfficeEmailBranding,
  useGetOfficeEmailBranding,
} from "repositories/office-repository";
import {
  generateMapImageWithMarker,
  handleError,
  handleSuccess,
  validateEmail,
  validateMobileNumber,
} from "utils";
import { v4 as uuidv4 } from "uuid";
import {
  BlobServiceClient,
  AnonymousCredential,
  newPipeline,
} from "@azure/storage-blob";
import { getCountries } from "repositories/utility-repository";
import { getSastoken } from "repositories/contract-repository";
import Loader from "components/Loader";
import Card from "components/Card";
import QuestimIcon from "assets/images/question_mark.svg";
import CompanyBrandingInfoModal from "pages/AccountPreferences/CompanyBrandingInfoModal";

const CompanyBranding = ({ t, history }) => {
  const profile = useSelector((state) => state.userProfile?.profile);
  const [isModalOpen, setModalOpen] = useState(false);
  const [showMapPredictions, setShowMapPredictions] = useState(false);
  const [previewType, setPreviewType] = useState("");
  const [errors, setErrors] = useState({});
  const [officeLogo, setOfficeLogo] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  // State to hold all branding values
  const [brandingValues, setBrandingValues] = useState({
    emailBrandingOfficeName: "",
    emailBrandingOfficeLatitude: 0,
    emailBrandingOfficeLongitude: 0,
    emailBrandingOfficeAddress: "",
    emailBrandingOfficeEmail: "",
    emailBrandingOfficeContactNumber: "",
    officeWebSiteLink: "",
    officeInstagramLink: "",
    officeLinkedinLink: "",
    officeFacebookLink: "",
    officeLocationRedirectionUrl: "",
    emailBrandingOfficeLogo: "",
    emailBrandingMapImage: "",
    isLocationEnabledForPatientAppointmentEmailReminder: false,
    isCalendarEventEnabledForPatientAppointmentEmailReminder: false,
    isLocationEnabledForPatientAppointmentConfirmationEmail: false,
    isCalendarEventEnabledForPatientAppointmentConfirmationEmail: false,
    isLocationEnableForPatientEmailRecallRemider: false,
    isLocationEnableForSystemEmailToPatientOwnerAndStaff: false,
  });

  // Fetch branding data from API
  const { isLoading, data: brandingData } = useGetOfficeEmailBranding(
    profile?.id
  );

  // Setup Google Places service
  const googleServices = usePlacesService({
    apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    debounce: 700,
  });

  useEffect(() => {
    getCountry();
  }, []);

  // Prefill form with API data or profile data
  useEffect(() => {
    if (brandingData) {
      // If API data is available, use it
      setBrandingValues((prevValues) => ({
        ...prevValues,
        emailBrandingOfficeName:
          brandingData.officeName || profile?.companyName || "",
        emailBrandingOfficeEmail:
          brandingData.officeEmail || profile?.emailId || "",
        emailBrandingOfficeContactNumber:
          brandingData.officeContactNumber || profile?.contactNumber || "",
        emailBrandingOfficeAddress:
          brandingData.officeAddress ||
          `${profile?.address || ""}, ${profile?.city || ""}, ${
            profile?.country || ""
          }`.trim(),
        emailBrandingOfficeLatitude: brandingData.officeLatitude || 0,
        emailBrandingOfficeLongitude: brandingData.officeLongitude || 0,
        officeWebSiteLink: brandingData.officeWebSiteLink || "",
        officeInstagramLink: brandingData.officeInstagramLink || "",
        officeLinkedinLink: brandingData.officeLinkedinLink || "",
        officeFacebookLink: brandingData.officeFacebookLink || "",
        officeLocationRedirectionUrl:
          brandingData.officeLocationRedirectionUrl || "",
        emailBrandingOfficeLogo: brandingData.emailBrandingOfficeLogo || "",
        emailBrandingMapImage: brandingData.emailBrandingMapImage || "",
        isLocationEnabledForPatientAppointmentEmailReminder:
          brandingData.isLocationEnabledForPatientAppointmentEmailReminder ||
          false,
        isCalendarEventEnabledForPatientAppointmentEmailReminder:
          brandingData.isCalendarEventEnabledForPatientAppointmentEmailReminder ||
          false,
        isLocationEnabledForPatientAppointmentConfirmationEmail:
          brandingData.isLocationEnabledForPatientAppointmentConfirmationEmail ||
          false,
        isCalendarEventEnabledForPatientAppointmentConfirmationEmail:
          brandingData.isCalendarEventEnabledForPatientAppointmentConfirmationEmail ||
          false,
        isLocationEnableForPatientEmailRecallRemider:
          brandingData.isLocationEnableForPatientEmailRecallRemider || false,
        isLocationEnableForSystemEmailToPatientOwnerAndStaff:
          brandingData.isLocationEnableForSystemEmailToPatientOwnerAndStaff ||
          false,
      }));

      // Set office logo if available
      if (brandingData.emailBrandingOfficeLogo) {
        setOfficeLogo(brandingData.emailBrandingOfficeLogo);
      }
    } else if (profile) {
      // If only profile data is available, use it for basic fields
      setBrandingValues((prevValues) => ({
        ...prevValues,
        emailBrandingOfficeName: profile.companyName || "",
        emailBrandingOfficeEmail: profile.emailId || "",
        emailBrandingOfficeContactNumber: profile.contactNumber || "",
        emailBrandingOfficeAddress: `${profile.address || ""}, ${
          profile.city || ""
        }, ${profile.country || ""}`.trim(),
      }));
    }
  }, [brandingData, profile]);

  // Effect to clear validation messages when values change
  useEffect(() => {
    const newErrors = { ...errors };

    // Clear errors for fields that have values
    if (
      brandingValues.emailBrandingOfficeName &&
      newErrors.emailBrandingOfficeName
    ) {
      delete newErrors.emailBrandingOfficeName;
    }

    if (
      brandingValues.emailBrandingOfficeAddress &&
      newErrors.emailBrandingOfficeAddress
    ) {
      delete newErrors.emailBrandingOfficeAddress;
    }

    if (
      brandingValues.emailBrandingOfficeContactNumber &&
      newErrors.emailBrandingOfficeContactNumber
    ) {
      delete newErrors.emailBrandingOfficeContactNumber;
    }

    if (
      brandingValues.emailBrandingOfficeEmail &&
      newErrors.emailBrandingOfficeEmail
    ) {
      delete newErrors.emailBrandingOfficeEmail;
    }

    if (
      (brandingValues.emailBrandingOfficeLogo || officeLogo) &&
      newErrors.officeLogo
    ) {
      delete newErrors.officeLogo;
    }

    // Only update errors state if something changed
    if (Object.keys(newErrors).length !== Object.keys(errors).length) {
      setErrors(newErrors);
    }
  }, [brandingValues, officeLogo]);

  // get country list
  const getCountry = async () => {
    try {
      let res = await getCountries();

      if (res?.length) {
        setCountryList(res);
      }
    } catch (error) {
      handleError(error);
    }
  };

  let selectedCountry = useMemo(
    () =>
      countryList?.find((e) => e.id === +profile?.countryId)?.name?.slice(0, 2),
    [profile?.country, countryList]
  );

  // Handle input changes
  const InputChange = (e) => {
    const { name, value } = e.target;
    setBrandingValues({
      ...brandingValues,
      [name]: value,
    });

    // Clear error for this field immediately
    if (errors[name]) {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setBrandingValues({
      ...brandingValues,
      [name]: checked,
    });
  };

  const handleLogoChange = async (e) => {
    e.preventDefault();
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }

    if (!files || !files.length) return;

    const file = files[0];
    const fsize = file?.size;
    const fileLimit = Math.round(fsize / 1024);
    let fileSizeLimit = true;
    const extFile = file?.type || "";

    if (fileLimit >= 1024 * 10) {
      setErrors({
        ...errors,
        officeLogo: "File size should be less than 10 MB.",
      });
      fileSizeLimit = false;
      return;
    }

    if (extFile !== "" && fileSizeLimit) {
      if (
        extFile === "image/jpeg" ||
        extFile === "image/jpg" ||
        extFile === "image/png"
      ) {
        setShowLoader(true);

        // First set the preview using FileReader
        const reader = new FileReader();
        reader.onload = () => {
          setOfficeLogo(reader.result);
          // Clear office logo error when a valid file is selected
          const newErrors = { ...errors };
          delete newErrors.officeLogo;
          setErrors(newErrors);
        };
        reader.readAsDataURL(file);

        // Then upload to Azure Blob Storage
        try {
          const sasToken = await getSastoken();
          const accountName = process.env.REACT_APP_AZURE_STORAGE_ACCOUNT;
          const sasString = sasToken;
          const pipeline = newPipeline(new AnonymousCredential());
          const containerName = process.env.REACT_APP_AZURE_STORAGE_CONTAINER;
          const fileExtension = file.name.split(".").pop();
          const blobServiceClient = new BlobServiceClient(
            `https://${accountName}.blob.core.windows.net?${sasString}`,
            pipeline
          );

          const containerClient =
            blobServiceClient.getContainerClient(containerName);
          const blobName = `${uuidv4()}.${fileExtension}`;
          const blockBlobClient = containerClient.getBlockBlobClient(blobName);
          const blobOptions = {
            blobHTTPHeaders: { blobContentType: file.type },
          };
          const uploadBlobResponse = await blockBlobClient.uploadData(
            file,
            blobOptions
          );

          if (
            uploadBlobResponse._response.status === 201 ||
            uploadBlobResponse._response.status === 200
          ) {
            const fileName =
              uploadBlobResponse._response.request.url.split("?")[0];

            // Update state with the Azure URL
            setBrandingValues((prevValues) => ({
              ...prevValues,
              emailBrandingOfficeLogo: fileName,
            }));
          }
        } catch (error) {
          console.error("Error uploading file:", error);
          setErrors({
            ...errors,
            officeLogo: "Error uploading file to server.",
          });
        } finally {
          setShowLoader(false);
        }
      } else {
        setErrors({
          ...errors,
          officeLogo: "Please select valid file",
        });
      }
    }
  };

  // Handle place selection
  const handlePlaceSelect = (
    placeId,
    description,
    selectedPlacedId,
    location
  ) => {
    try {
      // Get details for selected place
      googleServices.placesService?.getDetails({ placeId }, async (details) => {
        if (details || location) {
          setBrandingValues((prevValues) => ({
            ...prevValues,
            emailBrandingOfficeAddress:
              description || details?.formatted_address,
            emailBrandingOfficeLatitude: location?.lat
              ? location.lat
              : details?.geometry?.location?.lat(),
            emailBrandingOfficeLongitude: location?.long
              ? location.long
              : details?.geometry?.location?.lng(),
          }));

          // Clear address error when a valid address is selected
          if (errors.emailBrandingOfficeAddress) {
            const newErrors = { ...errors };
            delete newErrors.emailBrandingOfficeAddress;
            setErrors(newErrors);
          }
        }
      });
    } catch (error) {
      // Using handleError instead of toast directly, assuming you have this utility function
      handleError(error);
    }
  };

  const createMapLink = (latitude, longitude) => {
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
  };

  const createMapImage = async (
    brandAddress,
    brandLatitude,
    brandLongitude
  ) => {
    try {
      const sasToken = await getSastoken();

      const imageUrl = await generateMapImageWithMarker({
        address: brandAddress,
        latitude: brandLatitude,
        longitude: brandLongitude,
        sasToken: sasToken,
      });

      if (imageUrl) {
        return imageUrl; // Return the direct image URL
      }
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  };

  const goBack = () => history.push(constants.routes.accountPreferences);

  const placePredictionContent = () => {
    return googleServices.placePredictions?.map((place) => {
      const { description, place_id } = place;

      return (
        <li
          key={place_id}
          onClick={() => {
            setTimeout(() => {
              setShowMapPredictions(false);
              handlePlaceSelect(place_id, description);
            }, 300);
          }}
        >
          {description}
        </li>
      );
    });
  };

  const isValidBandingFields = () => {
    const {
      emailBrandingOfficeName,
      emailBrandingOfficeAddress,
      emailBrandingOfficeEmail,
      emailBrandingOfficeContactNumber,
      emailBrandingOfficeLogo,
    } = brandingValues;

    const newErrors = {};
    let isValid = true;

    if (!emailBrandingOfficeName) {
      newErrors.emailBrandingOfficeName = t("form.errors.emptyField", {
        field: t("form.fields.companyName"),
      });
      isValid = false;
    }

    if (!emailBrandingOfficeAddress) {
      newErrors.emailBrandingOfficeAddress = t("form.errors.emptyField", {
        field: t("form.fields.address"),
      });
      isValid = false;
    }

    if (
      emailBrandingOfficeAddress?.length > constants.wordLimits.ADDOFFICEADDRESS
    ) {
      newErrors.emailBrandingOfficeAddress = t(
        "form.errors.officeAddressLimit"
      );
      isValid = false;
    }

    if (!emailBrandingOfficeContactNumber) {
      newErrors.emailBrandingOfficeContactNumber = t("form.errors.emptyField", {
        field: t("form.fields.companyPhonenumber"),
      });
      isValid = false;
    }

    if (
      emailBrandingOfficeContactNumber &&
      !validateMobileNumber(emailBrandingOfficeContactNumber)
    ) {
      newErrors.emailBrandingOfficeContactNumber = t(
        "form.errors.invalidValue",
        {
          field: t("form.fields.companyPhonenumber"),
        }
      );
      isValid = false;
    }

    if (!emailBrandingOfficeEmail?.trim().length) {
      newErrors.emailBrandingOfficeEmail = t("form.errors.emptyField", {
        field: t("officeBranding.officeEmail"),
      });
      isValid = false;
    }

    if (emailBrandingOfficeEmail && !validateEmail(emailBrandingOfficeEmail)) {
      newErrors.emailBrandingOfficeEmail = t("form.errors.invalidValue", {
        field: t("form.fields.companyEmail"),
      });
      isValid = false;
    }

    // Add validation for office logo
    if (!emailBrandingOfficeLogo && !officeLogo) {
      newErrors.officeLogo = t("form.errors.emptyField", {
        field: t("form.fields.officeLogo"),
      });
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const saveCompanyBranding = async () => {
    if (!isValidBandingFields()) return;
    const {
      emailBrandingOfficeLatitude,
      emailBrandingOfficeLongitude,
      emailBrandingOfficeAddress,
    } = brandingValues;
    let payload = { ...brandingValues };
    if (emailBrandingOfficeLatitude && emailBrandingOfficeLongitude) {
      payload.officeLocationRedirectionUrl = createMapLink(
        emailBrandingOfficeLatitude,
        emailBrandingOfficeLongitude
      );
    }

    if (
      emailBrandingOfficeLatitude &&
      emailBrandingOfficeLongitude &&
      emailBrandingOfficeAddress
    ) {
      payload.emailBrandingMapImage =
        (await createMapImage(
          emailBrandingOfficeAddress,
          emailBrandingOfficeLatitude,
          emailBrandingOfficeLongitude
        )) || "";
    }
    try {
      setShowLoader(true);
      let res = await addOfficeEmailBranding(payload);

      handleSuccess(res.message);
      goBack();
    } catch (error) {
      handleError(error);
    } finally {
      setShowLoader(false);
    }
  };

  const renderCompanyBrandingForm = () => {
    return (
      <div className={styles["branding-container"]}>
        <div
          className={styles["branding_left_container"] + " " + "col-lg-8 p-0"}
        >
          <div className={styles["input_wrapper"]}>
            <Input
              Title={t("form.fields.companyName")}
              Type="text"
              Placeholder={t("form.placeholder1", {
                field: t("form.fields.companyName"),
              })}
              Name="emailBrandingOfficeName"
              HandleChange={InputChange}
              Error={errors.emailBrandingOfficeName}
              Value={brandingValues.emailBrandingOfficeName}
            />
            <Input
              Title={t("form.fields.companyEmail")}
              Type="text"
              Placeholder={t("form.placeholder1", {
                field: t("form.fields.companyEmail"),
              })}
              Name="emailBrandingOfficeEmail"
              HandleChange={InputChange}
              Error={errors.emailBrandingOfficeEmail}
              Value={brandingValues.emailBrandingOfficeEmail}
            />
            <Input
              Title={t("form.fields.companyPhonenumber")}
              MaxLength={15}
              Type="text"
              Placeholder={t("form.placeholder1", {
                field: t("form.fields.companyPhonenumber"),
              })}
              Name="emailBrandingOfficeContactNumber"
              HandleChange={InputChange}
              Error={errors.emailBrandingOfficeContactNumber}
              Value={brandingValues.emailBrandingOfficeContactNumber}
            />
            <div className={styles["location-input-wrapper"]}>
              <Input
                Title={t("form.fields.address")}
                Type="text"
                Placeholder={t("form.placeholder1", {
                  field: t("form.fields.address"),
                })}
                Name="emailBrandingOfficeAddress"
                HandleChange={(e) => {
                  InputChange(e);
                  if (!showMapPredictions) {
                    setShowMapPredictions(true);
                  }
                  googleServices.getPlacePredictions({
                    input: e.target.value,
                    componentRestrictions: {
                      country: selectedCountry,
                    },
                  });
                }}
                Error={errors.emailBrandingOfficeAddress}
                Value={brandingValues.emailBrandingOfficeAddress}
              />
              <ul
                className={
                  styles["location-list"] +
                  `${!showMapPredictions ? " d-none" : ""}`
                }
              >
                {showMapPredictions && placePredictionContent()}
              </ul>
            </div>
            <Input
              Title={t("officeBranding.websiteLink")}
              MaxLength={1000}
              Type="text"
              Placeholder={t("form.placeholder1", {
                field: t("officeBranding.websiteLink"),
              })}
              Name="officeWebSiteLink"
              HandleChange={InputChange}
              Value={brandingValues.officeWebSiteLink}
            />
            <Input
              Title={t("officeBranding.instagramLink")}
              MaxLength={500}
              Type="text"
              Placeholder={t("form.placeholder1", {
                field: t("officeBranding.instagramLink"),
              })}
              Name="officeInstagramLink"
              HandleChange={InputChange}
              Value={brandingValues.officeInstagramLink}
            />
            <Input
              Title={t("officeBranding.facebookLink")}
              MaxLength={1000}
              Type="text"
              Placeholder={t("form.placeholder1", {
                field: t("officeBranding.facebookLink"),
              })}
              Name="officeFacebookLink"
              HandleChange={InputChange}
              Value={brandingValues.officeFacebookLink}
            />
            <Input
              Title={t("officeBranding.linkedinLink")}
              MaxLength={1000}
              Type="text"
              Placeholder={t("form.placeholder1", {
                field: t("officeBranding.linkedinLink"),
              })}
              Name="officeLinkedinLink"
              HandleChange={InputChange}
              Value={brandingValues.officeLinkedinLink}
            />

            <div>
              <Text
                size="16px"
                marginBottom="20px"
                weight="600"
                color="#587E85"
                className={`${styles["mb-30"]}`}
              >
                {t("accountPreferences.addMoreElementsToEmail")}
              </Text>
              <div className={styles.preview_wrapper}>
                <div className={styles["check_box_container"]}>
                  <p
                    className={styles["checkbox_preview_text"]}
                    onClick={() => {
                      setPreviewType("systemEmailsToPateint");
                      setModalOpen(true);
                    }}
                  >
                    {t("officeBranding.preview")}
                  </p>
                </div>
                <div className={styles["checkbox_wrapper"]}>
                  <div className="ch-checkbox c-field all-event-checkbox m-0">
                    <label>
                      <input
                        onChange={handleCheckboxChange}
                        name="isLocationEnableForSystemEmailToPatientOwnerAndStaff"
                        type="checkbox"
                        checked={
                          brandingValues.isLocationEnableForSystemEmailToPatientOwnerAndStaff
                        }
                      />
                      <span>{t("officeBranding.locationOnMap")}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles["file-upload-container"] + " " + " col-lg-4"}>
          <div className={styles["file-upload-field"]}>
            <div className={`${styles["office-image"]} ${styles["office-image-brand-set"]}`}>
              {officeLogo ? (
                <img src={officeLogo} alt="upload" />
              ) : (
                <img src={cameraIconWithBg} alt="upload" />
              )}
            </div>
            <div
              className={`${styles["ch-upload-button"]}  ${styles["ch-upload-button"]} ${styles["brand-edit-upload-btn"]}`}
            >
              <input
                id="fileUpload-officeLogo"
                type="file"
                onChange={handleLogoChange}
              />
              <span>
                <img
                  width="32px"
                  height="32px"
                  src={require("assets/images/upload-image.svg").default}
                  alt="upload"
                />
              </span>
            </div>
          </div>
          <div className={styles["upload-help-text"] + " " + "mt-4"}>
            {t("accountOwner.uploadOfficeLogoDescription")}
          </div>
          {errors.officeLogo && (
            <span className="error-msg text-center">{errors.officeLogo}</span>
          )}
        </div>
        {isModalOpen && (
          <BrandEmailTemplate
            isModalOpen={isModalOpen}
            closeModal={() => setModalOpen(false)}
            modalContent={brandingValues}
            officeLogo={officeLogo}
            previewType={previewType}
          />
        )}
      </div>
    );
  };

  const renderFooter = () => (
    <div className={styles.branding_btn}>
      <button
        className="button button-round button-shadow"
        title={t("continue")}
        onClick={() => saveCompanyBranding()}
      >
        {t("save")}
      </button>

      <button
        onClick={() => goBack()}
        className="button button-round button-border button-dark"
        title={t("cancel")}
      >
        {t("cancel")}
      </button>
    </div>
  );

  return (
    <Page
      onBack={() => {
        goBack();
      }}
      className={accountPrefrencesStyles["global-main-page"]}
      title={t("accountPreferences.companyBrandingSettings")}
      actionButton={
        <button
          type="button"
          className={styles.question_btn}
          onClick={() => setIsInfoModalOpen(true)}
        >
          <img src={QuestimIcon} alt="QuestimIcon" />
          <p className="m-0">{t("navbar.help")}</p>
        </button>
      }
    >
      {(showLoader || isLoading) && <Loader />}
      {/* {renderHeader()} */}
      <Card className={styles.company_branding_card}>
        {renderCompanyBrandingForm()}
        {renderFooter()}
      </Card>
      {isInfoModalOpen && (
        <CompanyBrandingInfoModal
          isOpen={isInfoModalOpen}
          onClose={setIsInfoModalOpen}
        />
      )}
    </Page>
  );
};

export default withTranslation()(CompanyBranding);
